/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : LoginHelpModal.tsx
// 설명   : 로그인 화면의 도움말(?) 아이콘 클릭 시 뜨는 팝업 - 비밀번호변경/2차인증/로그인 안 될 때 등을
//          고객사 일반 사용자 기준으로(내부 함수명/DB 용어 없이) 설명함. LoginPage.tsx 전용이라 디자인
//          토큰은 loginModern.css의 --lp-* 변수를 그대로 재사용함(새 CSS 파일 없이 이 파일 안에서 styled
//          형태로 inline style만 사용 - 로그인 화면은 별도 전역 CSS 의존을 늘리지 않는 편이 안전해서).
// 작성일 : 2026-10-01
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';

interface LoginHelpModalProps {
  open: boolean;
  onClose: () => void;
}

interface HelpSection {
  icon: string;
  iconBg: string;
  title: string;
  body: React.ReactNode;
}

const sections: HelpSection[] = [
  {
    icon: 'fa-key',
    iconBg: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    title: '"비밀번호를 변경해주세요" 화면이 뜨는 경우',
    body: (
      <>
        <p>관리자가 처음 계정을 만들면서 임시 비밀번호를 줬거나, 비밀번호를 오래 바꾸지 않아 사용기한이 지난 경우에 뜹니다.</p>
        <p>새 비밀번호를 한 번 입력하면 바로 로그인이 이어서 진행됩니다 — 다시 로그인할 필요 없습니다.</p>
      </>
    ),
  },
  {
    icon: 'fa-shield-halved',
    iconBg: 'linear-gradient(135deg, #0ea5e9, #4f46e5)',
    title: '"추가 인증(2차인증)" 화면이 뜨는 경우',
    body: (
      <>
        <p>비밀번호만으로는 부족할 수 있어, 계정을 더 안전하게 지키기 위한 한 단계입니다.</p>
        <ul>
          <li><b>처음이라면</b> : 인증 방법을 고르는 화면이 뜹니다 — 휴대폰 OTP 앱(인증 앱)을 쓰거나, 등록된 이메일로 코드를 받는 방법 중 선택할 수 있습니다. 둘 중 하나로 처음 한 번만 등록하면 됩니다.</li>
          <li><b>이미 등록했다면</b> : 매번 로그인할 때 인증 앱의 숫자코드를 입력하거나, 이메일로 온 코드를 입력합니다.</li>
          <li><b>"이 기기 기억하기"</b>를 체크하면, 정해진 기간 동안은 같은 기기/브라우저에서 다시 묻지 않습니다.</li>
        </ul>
      </>
    ),
  },
  {
    icon: 'fa-unlock-keyhole',
    iconBg: 'linear-gradient(135deg, #10b981, #0ea5e9)',
    title: '비밀번호를 잊어버렸을 때 (비밀번호 찾기)',
    body: (
      <>
        <p>비밀번호는 원래 값을 알려주지 않고, <b>본인확인 후 새 비밀번호를 만드는 방식</b>으로 다시 설정합니다.</p>
        <ul>
          <li><b>1단계 · 아이디 입력</b> : 로그인 화면의 "비밀번호를 잊으셨나요?"를 누르고 아이디를 입력하고 <b>다음</b>을 누릅니다.</li>
          <li><b>2단계 · 본인확인 방법 선택</b> : 아래 두 가지 중 하나를 고릅니다.
            <ul>
              <li><b>OTP 앱 코드로 확인</b> : 인증 앱(Google/MS Authenticator 등)에 지금 표시된 6자리 숫자를 입력합니다. OTP 앱을 등록해 둔 경우에만 됩니다.</li>
              <li><b>등록 이메일로 확인</b> : 계정에 등록된 이메일로 6자리 코드가 발송됩니다. 코드는 <b>5분</b> 동안만 유효합니다.</li>
            </ul>
          </li>
          <li><b>3단계 · 새 비밀번호 설정</b> : 본인확인을 통과하면 새 비밀번호를 두 번 입력합니다. 완료되면 기존에 로그인돼 있던 기기는 모두 로그아웃되니, 새 비밀번호로 다시 로그인하세요.</li>
        </ul>
        <p><b>새 비밀번호 규칙</b> : 10자 이상, 영문 대문자·소문자·숫자·특수문자 중 3종류 이상 조합, 아이디를 포함하지 않아야 하며, 최근 5번 안에 쓴 비밀번호는 다시 쓸 수 없습니다.</p>
        <p><b>인증코드가 안 올 때</b> : 스팸함을 먼저 확인하세요. 코드 발송은 <b>1시간에 5번</b>까지 가능하고, 화면에 "1시간 내 남은 발송 횟수"가 표시됩니다. 취소 후 다시 눌러도 횟수 안에서는 매번 발송됩니다.</p>
        <p><b>인증이 여러 번 틀렸을 때</b> : 인증 코드를 <b>5번 틀리면 잠시 차단</b>됩니다. 등록 이메일로 새 코드를 받으면 다시 시도할 수 있습니다.</p>
        <p><b>둘 다 안 될 때</b> : OTP 앱도 등록하지 않았고 이메일도 없으면 셀프로는 재설정할 수 없습니다. 관리자에게 요청하세요(관리자가 임시 비밀번호를 발급하거나 2차인증을 초기화해 줄 수 있습니다).</p>
        <p><b>아이디가 맞는지 확인해 주세요</b> : 입력한 아이디가 없어도 화면은 같게 진행되지만, 본인확인 단계에서 넘어가지 않습니다. 회사를 잘못 골랐는지도 함께 확인하세요.</p>
      </>
    ),
  },
  {
    icon: 'fa-triangle-exclamation',
    iconBg: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    title: '로그인이 안 될 때 흔한 이유',
    body: (
      <ul>
        <li><b>비밀번호를 여러 번 잘못 입력함</b> : 보호를 위해 일정 횟수 이상 틀리면 계정이 잠깁니다 → 관리자에게 잠금 해제를 요청하세요.</li>
        <li><b>계정이 사용중지 상태</b> : 퇴사/직무변경 등으로 관리자가 계정을 막아둔 경우입니다 → 관리자에게 문의하세요.</li>
        <li><b>허용되지 않은 곳에서 접속</b> : 일부 계정은 특정 네트워크(예: 사무실)에서만 접속하도록 제한돼 있을 수 있습니다 → 관리자에게 접속 환경을 확인하세요.</li>
        <li><b>동시 사용 인원이 다 찬 경우</b> : 회사의 동시 접속 가능 인원이 초과된 경우입니다 → 잠시 후 다시 시도하거나 관리자에게 문의하세요.</li>
      </ul>
    ),
  },
  {
    icon: 'fa-circle-info',
    iconBg: 'linear-gradient(135deg, #64748b, #334155)',
    title: '그 외 안내',
    body: (
      <ul>
        <li>"로그인 정보 저장"을 체크하면 다음에 접속할 때 아이디가 자동으로 입력됩니다(비밀번호는 저장되지 않습니다).</li>
        <li>비밀번호나 인증코드는 본인만 알아야 하며, 동료나 타인과 공유하지 마세요.</li>
      </ul>
    ),
  },
];

const LoginHelpModal: React.FC<LoginHelpModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          style: {
            borderRadius: 20,
            overflow: 'hidden',
            fontFamily: "'Segoe UI', 'Apple SD Gothic Neo', 'Malgun Gothic', Tahoma, Geneva, Verdana, sans-serif",
          },
        },
      }}
    >
      {/* 헤더 - 브랜드 컬러(인디고->퍼플) 그라데이션, 로그인 폼의 제출버튼과 동일 톤 */}
      <div style={{
        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
        color: '#fff', padding: '22px 24px', position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flex: '0 0 auto',
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>
            <i className="fas fa-circle-question" aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>로그인 도움말</div>
            <div style={{ fontSize: 12.5, opacity: 0.85, marginTop: 2 }}>로그인 중 추가 화면이 뜨는 이유와 대처방법을 안내합니다</div>
          </div>
        </div>
        <IconButton
          aria-label="닫기"
          onClick={onClose}
          size="small"
          style={{ position: 'absolute', top: 14, right: 14, color: '#fff', background: 'rgba(255,255,255,0.14)' }}
        >
          <i className="fas fa-xmark" aria-hidden="true" style={{ fontSize: 15 }} />
        </IconButton>
      </div>

      {/* 본문 - 섹션별 아이콘 칩 + 설명 카드 */}
      <div style={{ padding: '20px 24px 24px', maxHeight: '65vh', overflowY: 'auto', background: '#f8fafc' }}>
        <style>{`
          .lp-help-body p { margin: 0 0 8px; }
          .lp-help-body p:last-child { margin-bottom: 0; }
          .lp-help-body ul { margin: 0; padding-left: 18px; }
          .lp-help-body li { margin-bottom: 6px; }
          .lp-help-body li:last-child { margin-bottom: 0; }
        `}</style>
        {sections.map((s, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex', gap: 14,
              background: '#fff', borderRadius: 14, padding: '16px 18px',
              marginBottom: idx < sections.length - 1 ? 14 : 0,
              border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: 11, flex: '0 0 auto',
              background: s.iconBg, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
              boxShadow: '0 4px 10px rgba(79,70,229,0.25)',
            }}>
              <i className={`fas ${s.icon}`} aria-hidden="true" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: '#334155' }}>
                <div className="lp-help-body">{s.body}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Dialog>
  );
};

export default LoginHelpModal;
